import { Component, computed, DestroyRef, ElementRef, inject, input, signal } from '@angular/core';

/**
 * - IDLE: a slow wave of light running around the frame
 * - CHASE: bulbs chasing each other around, quickly
 * - DIM: the bulbs turned down low
 */
export type TLingoMarqueeMood = 'IDLE' | 'CHASE' | 'DIM';

// The darker bevel along the outer edge (in px), which the bulbs stay clear of. Matches the host's inset shadow.
const BEVEL = 12;

interface TBulb {
  x: number;
  y: number;
  phase: number;
}

const WARM_BULB = 'bg-radial-[at_40%_35%] from-white from-25% via-[#fff1b5] via-45% to-[#ffcf4a]';
const WARM_GLOW = 'shadow-[0_0_14px_3px_rgb(255_214_102/0.9)]';
const BULB_MOODS: Record<TLingoMarqueeMood, string> = {
  // A wave of light running around the frame
  IDLE: `${WARM_BULB} ${WARM_GLOW} anim-twinkle [animation-delay:calc(var(--i)*-0.12s)]`,
  CHASE: `${WARM_BULB} ${WARM_GLOW} anim-chase [animation-delay:calc(var(--phase)*-0.2s)]`,
  DIM: `${WARM_BULB} brightness-50 saturate-140`,
};

/**
 * The orange marquee frame with light bulbs from the Lingo studio, wrapped around its content. The bulbs are separate elements, spread evenly along the frame for whatever size it gets, so they can be animated.
 */
@Component({
  selector: 'rid-lingo-marquee',
  templateUrl: './rid-lingo-marquee.html',
  host: {
    // The padding is the frame and the dark inset shadow is the BEVEL
    class:
      'relative block box-border rounded-[56px] p-11 bg-linear-to-b from-[#f7a541] to-[#e36a26] shadow-[inset_0_0_0_5px_#ffc06a,inset_0_0_0_12px_#d4561d,0_16px_48px_rgb(0_0_0/0.35)]',
  },
})
export class RidLingoMarquee {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  mood = input<TLingoMarqueeMood>('IDLE');
  /** The distance between the bulbs, in px. */
  bulbSpacing = input<number>(80);

  bulbClass = computed(() => BULB_MOODS[this.mood()]);

  // In px, unscaled
  private readonly size = signal({ width: 0, height: 0, frame: 0, radius: 0 });

  bulbs = computed<TBulb[]>(() => {
    const { width, height, frame, radius } = this.size();
    if (width === 0 || height === 0) return [];

    // The bulbs run along the middle of the flat part of the frame (between the bevel and the content), which is a rounded rectangle itself
    const inset = (BEVEL + frame) / 2;
    const r = radius - inset;
    const w = width - 2 * inset;
    const h = height - 2 * inset;
    const straightW = w - 2 * r;
    const straightH = h - 2 * r;
    const arc = (Math.PI / 2) * r;

    // Walks clockwise from the top left corner (where the top edge starts), as a chain of straight edges and corners
    const segments: ((t: number) => [number, number])[] = [
      (t) => [r + t * straightW, 0],
      (t) => corner(w - r, r, -Math.PI / 2 + (t * Math.PI) / 2),
      (t) => [w, r + t * straightH],
      (t) => corner(w - r, h - r, (t * Math.PI) / 2),
      (t) => [w - r - t * straightW, h],
      (t) => corner(r, h - r, Math.PI / 2 + (t * Math.PI) / 2),
      (t) => [0, h - r - t * straightH],
      (t) => corner(r, r, Math.PI + (t * Math.PI) / 2),
    ];
    const lengths = [straightW, arc, straightH, arc, straightW, arc, straightH, arc];
    const total = lengths.reduce((sum, l) => sum + l, 0);

    function corner(cx: number, cy: number, angle: number): [number, number] {
      return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
    }

    // A multiple of 3, so the chase pattern loops around seamlessly
    const count = Math.max(3, Math.round(total / this.bulbSpacing() / 3) * 3);

    return Array.from({ length: count }, (_, i) => {
      let distance = (i / count) * total;
      let segment = 0;
      while (distance > lengths[segment] && segment < segments.length - 1) {
        distance -= lengths[segment];
        segment++;
      }

      const [x, y] = segments[segment](lengths[segment] === 0 ? 0 : distance / lengths[segment]);
      return { x: x + inset, y: y + inset, phase: i % 3 };
    });
  });

  constructor() {
    const observer = new ResizeObserver(() => {
      const element = this.host.nativeElement;
      const style = getComputedStyle(element);
      this.size.set({
        width: element.offsetWidth,
        height: element.offsetHeight,
        frame: parseFloat(style.paddingTop),
        radius: parseFloat(style.borderTopLeftRadius),
      });
    });
    observer.observe(this.host.nativeElement);
    inject(DestroyRef).onDestroy(() => observer.disconnect());
  }
}
